
import React, { useState, useEffect } from 'react';
import { Check, ChevronDown, Filter } from 'lucide-react';
import BlurOverlay from '../ui/BlurOverlay';

// Filter types based on MANN-FILTER categories
const filterTypes = [
  {
    type: 'Air Filter',
    description: 'Protects your engine from dust and particles',
    prefix: 'C'  // MANN uses C prefix for air filters
  },
  {
    type: 'Oil Filter',
    description: 'Removes contaminants from engine oil',
    prefix: 'W'  // MANN uses W prefix for oil filters
  },
  {
    type: 'Fuel Filter',
    description: 'Ensures clean fuel delivery',
    prefix: 'WK' // MANN uses WK prefix for fuel filters
  },
  {
    type: 'Cabin Air Filter',
    description: 'Keeps your cabin air clean',
    prefix: 'CU' // MANN uses CU prefix for cabin filters
  }
];

// Common filter sizes based on MANN-FILTER specifications
const filterSizes = {
  'Air Filter': ['Length: 200-350mm', 'Height: 50-100mm', 'Width: 150-300mm'],
  'Oil Filter': ['Height: 80-120mm', 'Diameter: 65-95mm'],
  'Fuel Filter': ['Height: 140-180mm', 'Diameter: 70-90mm'],
  'Cabin Air Filter': ['Length: 200-400mm', 'Width: 150-250mm', 'Height: 30-50mm']
};

// Car related data from CarForm
const carMakes = [
  'Toyota', 'Honda', 'Hyundai', 'Kia', 'Mazda', 'Nissan', 'Mercedes-Benz', 'BMW', 'Audi', 'Volkswagen',
  'Ford', 'Chevrolet', 'Jeep', 'Dodge', 'Ram', 'Subaru', 'Lexus', 'Acura', 'Infiniti', 'Genesis'
];

// Car logo mapping
const carLogos: Record<string, string> = {
  'Toyota': 'https://www.carlogos.org/car-logos/toyota-logo.png',
  'Honda': 'https://www.carlogos.org/car-logos/honda-logo.png',
  'Hyundai': 'https://www.carlogos.org/car-logos/hyundai-logo.png',
  'Kia': 'https://www.carlogos.org/car-logos/kia-logo.png',
  'Mazda': 'https://www.carlogos.org/car-logos/mazda-logo.png',
  'Nissan': 'https://www.carlogos.org/car-logos/nissan-logo.png',
  'Mercedes-Benz': 'https://www.carlogos.org/car-logos/mercedes-benz-logo.png',
  'BMW': 'https://www.carlogos.org/car-logos/bmw-logo.png',
  'Audi': 'https://www.carlogos.org/car-logos/audi-logo.png',
  'Volkswagen': 'https://www.carlogos.org/car-logos/volkswagen-logo.png',
  'Ford': 'https://www.carlogos.org/car-logos/ford-logo.png',
  'Chevrolet': 'https://www.carlogos.org/car-logos/chevrolet-logo.png',
  'Jeep': 'https://www.carlogos.org/car-logos/jeep-logo.png',
  'Dodge': 'https://www.carlogos.org/car-logos/dodge-logo.png',
  'Ram': 'https://www.carlogos.org/car-logos/ram-logo.png',
  'Subaru': 'https://www.carlogos.org/car-logos/subaru-logo.png',
  'Lexus': 'https://www.carlogos.org/car-logos/lexus-logo.png',
  'Acura': 'https://www.carlogos.org/car-logos/acura-logo.png',
  'Infiniti': 'https://www.carlogos.org/car-logos/infiniti-logo.png',
  'Genesis': 'https://www.carlogos.org/car-logos/genesis-logo.png'
};

const carModels = {
  'Toyota': ['Corolla', 'Camry', 'RAV4', 'Highlander', 'Land Cruiser', 'Prius', 'Tacoma', 'Tundra'],
  'Honda': ['Civic', 'Accord', 'CR-V', 'Pilot', 'HR-V', 'Odyssey', 'Fit', 'Ridgeline'],
  'Hyundai': ['Elantra', 'Sonata', 'Tucson', 'Santa Fe', 'Kona', 'Palisade', 'Venue', 'Accent'],
  'Kia': ['Optima', 'Forte', 'Sportage', 'Sorento', 'Soul', 'Telluride', 'Seltos', 'Rio'],
  'Mazda': ['Mazda3', 'Mazda6', 'CX-5', 'CX-9', 'MX-5', 'CX-3', 'CX-30', 'CX-50'],
  'Nissan': ['Altima', 'Maxima', 'Rogue', 'Murano', 'Pathfinder', 'Sentra', 'Versa', 'Frontier'],
  'Mercedes-Benz': ['A-Class', 'C-Class', 'E-Class', 'S-Class', 'GLC', 'GLE', 'GLS', 'G-Class'],
  'BMW': ['3 Series', '5 Series', '7 Series', 'X3', 'X5', 'X1', 'X7', 'i4'],
  'Audi': ['A3', 'A4', 'A6', 'Q5', 'Q7', 'e-tron', 'Q3', 'A5'],
  'Volkswagen': ['Golf', 'Jetta', 'Passat', 'Tiguan', 'Atlas', 'ID.4', 'Taos', 'Arteon'],
  'Ford': ['F-150', 'Mustang', 'Explorer', 'Escape', 'Edge', 'Bronco', 'Maverick', 'Ranger'],
  'Chevrolet': ['Silverado', 'Equinox', 'Malibu', 'Tahoe', 'Traverse', 'Camaro', 'Suburban', 'Trax'],
  'Jeep': ['Wrangler', 'Cherokee', 'Grand Cherokee', 'Compass', 'Renegade', 'Gladiator'],
  'Dodge': ['Charger', 'Challenger', 'Durango', 'Journey', 'Hornet'],
  'Ram': ['1500', '2500', '3500', 'ProMaster'],
  'Subaru': ['Outback', 'Forester', 'Crosstrek', 'Impreza', 'Legacy', 'Ascent', 'WRX'],
  'Lexus': ['RX', 'ES', 'NX', 'IS', 'GX', 'UX', 'LS'],
  'Acura': ['MDX', 'RDX', 'TLX', 'ILX', 'NSX', 'Integra'],
  'Infiniti': ['Q50', 'QX60', 'QX80', 'QX50', 'Q60'],
  'Genesis': ['G70', 'G80', 'G90', 'GV70', 'GV80']
};

const years = Array.from({ length: 30 }, (_, i) => 2024 - i);

const engineTypes = [
  'Gasoline', // ADDED!
  '4-cylinder', 
  '6-cylinder', 
  '8-cylinder', 
  'Hybrid', 
  'Electric', 
  'Diesel', 
  'Turbo 4-cylinder',
  'Twin-Turbo V6',
  'V8',
  'Inline-6'
];

type CarModels = {
  [key: string]: string[];
};

interface FilterFormProps {
  onSubmit?: (data: any) => void;
}

const FilterForm = ({ onSubmit }: FilterFormProps) => {
  const [selectedFilterType, setSelectedFilterType] = useState('');
  const [filterSize, setFilterSize] = useState('');
  const [currentNumber, setCurrentNumber] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [engineType, setEngineType] = useState('');
  
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
  const [makeDropdownOpen, setMakeDropdownOpen] = useState(false);
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [engineDropdownOpen, setEngineDropdownOpen] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (onSubmit) {
      onSubmit({
        filterType: selectedFilterType,
        filterSize,
        currentNumber,
        make,
        model,
        year,
        engineType
      });
    }
  };

  return (
    <BlurOverlay className="max-w-4xl mx-auto">
      <div className="flex flex-col lg:flex-row items-start space-y-6 lg:space-y-0 lg:space-x-8">
        <div className="w-full lg:w-5/12">
          <h3 className="text-2xl font-bold text-autocare-blue mb-2">Find Your Perfect Filter</h3>
          <p className="text-gray-600 mb-4">
            Tell us about your vehicle and the filter you need, and we'll help you find the right match.
          </p>
          <div className="relative pt-4 hidden lg:block">
            <div className="p-4 bg-autocare-blue/10 rounded-xl">
              <div className="flex items-center">
                <Filter className="h-8 w-8 text-autocare-blue mr-4" />
                <div>
                  <div className="text-sm text-gray-600">Selected Filter Type</div>
                  <div className="font-semibold">{selectedFilterType || "Not selected yet"}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full lg:w-7/12">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Car Make Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Car Make</label>
              <button
                type="button"
                className="input-field text-left flex items-center justify-between"
                onClick={() => setMakeDropdownOpen(!makeDropdownOpen)}
              >
                <div className="flex items-center">
                  {make && carLogos[make] && (
                    <img 
                      src={carLogos[make]} 
                      alt={`${make} logo`}
                      className="h-6 w-6 mr-2 object-contain"
                    />
                  )}
                  <span>{make || 'Select Car Make'}</span>
                </div>
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </button>
              {makeDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
                  {carMakes.map((item) => (
                    <div
                      key={item}
                      className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between"
                      onClick={() => {
                        setMake(item);
                        setModel('');
                        setMakeDropdownOpen(false);
                      }}
                    >
                      <div className="flex items-center">
                        {carLogos[item] && (
                          <img 
                            src={carLogos[item]} 
                            alt={`${item} logo`}
                            className="h-6 w-6 mr-2 object-contain"
                          />
                        )}
                        <span>{item}</span>
                      </div>
                      {make === item && <Check className="h-4 w-4 text-autocare-blue" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Car Model Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Car Model</label>
              <button
                type="button"
                className={`input-field text-left flex items-center justify-between ${!make ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                onClick={() => make && setModelDropdownOpen(!modelDropdownOpen)}
                disabled={!make}
              >
                <span>{model || 'Select Car Model'}</span>
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </button>
              {modelDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
                  {(carModels as CarModels)[make]?.map((item) => (
                    <div
                      key={item}
                      className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between"
                      onClick={() => {
                        setModel(item);
                        setModelDropdownOpen(false);
                      }}
                    >
                      {item}
                      {model === item && <Check className="h-4 w-4 text-autocare-blue" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Year Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <button
                type="button"
                className="input-field text-left flex items-center justify-between"
                onClick={() => setYearDropdownOpen(!yearDropdownOpen)}
              >
                <span>{year || 'Select Year'}</span>
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </button>
              {yearDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
                  {years.map((item) => (
                    <div
                      key={item}
                      className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between"
                      onClick={() => {
                        setYear(item.toString());
                        setYearDropdownOpen(false);
                      }}
                    >
                      {item}
                      {year === item.toString() && <Check className="h-4 w-4 text-autocare-blue" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Engine Type Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">Engine Type</label>
              <button
                type="button"
                className="input-field text-left flex items-center justify-between"
                onClick={() => setEngineDropdownOpen(!engineDropdownOpen)}
              >
                <span>{engineType || 'Select Engine Type'}</span>
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </button>
              {engineDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
                  {engineTypes.map((item) => (
                    <div
                      key={item}
                      className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between"
                      onClick={() => {
                        setEngineType(item);
                        setEngineDropdownOpen(false);
                      }}
                    >
                      {item}
                      {engineType === item && <Check className="h-4 w-4 text-autocare-blue" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Filter Type Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                What type of filter are you looking for?
              </label>
              <button
                type="button"
                className="input-field text-left flex items-center justify-between"
                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
              >
                <span>{selectedFilterType || 'Select Filter Type'}</span>
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </button>
              {filterDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
                  {filterTypes.map((filter) => (
                    <div
                      key={filter.type}
                      className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer"
                      onClick={() => {
                        setSelectedFilterType(filter.type);
                        setFilterDropdownOpen(false);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{filter.type}</div>
                          <div className="text-sm text-gray-500">{filter.description}</div>
                        </div>
                        {selectedFilterType === filter.type && (
                          <Check className="h-4 w-4 text-autocare-blue" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Current Filter Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Filter Number (Optional)
              </label>
              <input
                type="text"
                className="input-field"
                placeholder="e.g., W 719/45 (Found on your current filter)"
                value={currentNumber}
                onChange={(e) => setCurrentNumber(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                Tip: The filter number can usually be found printed on your current filter
              </p>
            </div>

            {/* Filter Size Selection */}
            {selectedFilterType && (
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter Size
                </label>
                <button
                  type="button"
                  className="input-field text-left flex items-center justify-between"
                  onClick={() => setSizeDropdownOpen(!sizeDropdownOpen)}
                >
                  <span>{filterSize || 'Select Size'}</span>
                  <ChevronDown className="h-5 w-5 text-gray-400" />
                </button>
                {sizeDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto border border-gray-200">
                    {filterSizes[selectedFilterType as keyof typeof filterSizes].map((size) => (
                      <div
                        key={size}
                        className="px-4 py-2 hover:bg-autocare-gray-light cursor-pointer flex items-center justify-between"
                        onClick={() => {
                          setFilterSize(size);
                          setSizeDropdownOpen(false);
                        }}
                      >
                        {size}
                        {filterSize === size && <Check className="h-4 w-4 text-autocare-blue" />}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full mt-6"
            >
              Find Compatible Filters
            </button>
          </form>
        </div>
      </div>
    </BlurOverlay>
  );
};

export default FilterForm;
